import * as i0 from '@angular/core';
import { Directive, ViewChild, ViewChildren } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { Subscription, BehaviorSubject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { filterStateChange } from '@fabric/components/table-filter/data-query';
import { BasePageComponent } from '@satin/core/components/base-page';
import { GridConstant, SharedConstant } from '@satin/core/constant';
import { isNotNull, isNotEmptyArray, isEmptyArray } from '@satin/core/utility';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
/**
 * Commonly used methods for Fab table which can be used by any component which extends this class.
 * Note: this is not a regular Angular Directive (the '@Directive' annotation is added because we are
 * using angular features '@ViewChild'). As of v10, a Directive or Component annotation is required when
 * using 'angular features' in a base class.
 *
 * References:
 * https://v9.angular.io/guide/deprecations#undecorated-base-classes-using-angular-features
 * https://angular.io/guide/migration-undecorated-classes
 *
 *
 * @packageDocumentation
 */
class CommonFabGridComponent extends BasePageComponent {
    /**
     * Constructor
     * @param overrideDelay
     */
    constructor(overrideDelay) {
        super();
        /**
         * flag to detect grid loading state
         */
        this.isGridLoading = false;
        /**
         * Default page size
         */
        this.pageSizeOptions = [10, 25, 50];
        /**
         * Tracking selected row
         */
        this.selectedRowIndex = -1;
        /**
         * string filter operators
         */
        this.stringOperators = GridConstant.FAB_STRING_FILTER_OPERATOR;
        /**
         * number filter operators
         */
        this.numberOperators = GridConstant.FAB_NUMBER_FILTER_OPERATOR;
        /**
         * date filter operators
         */
        this.dateOperators = GridConstant.FAB_DATE_FILTER_OPERATOR;
        /**
         * Default page size
         */
        this.pageSize = 10;
        /**
         * Default page length
         */
        this.resultsLength = 1;
        /**
         * Subscription
         * @protected
         */
        this.subscriptions = new Subscription();
        /**
         * Fab grid state BehaviorSubject
         * @protected
         */
        this.stateSubject = new BehaviorSubject(this.getState());
        /**
         * default table state
         * @private
         */
        this.defaultState = {
            skip: 0,
            take: this.pageSize,
            filter: [],
            sort: [],
        };
        this.dataSource = new MatTableDataSource([]);
        this.dataSource.sort = this.sort;
        this.primaryKeyName = SharedConstant.PRIMARY_KEY_PARAMETER_DEFAULT;
        // Delay for Fab grid dataStateChange event of xxxx ms
        const timeDelay = overrideDelay | SharedConstant.DEFAULT_GRID_FILTERING_DELAY;
        this.setState(this.defaultState);
        this.subscriptions.add(this.stateSubject.pipe(debounceTime(timeDelay))
            .subscribe(s => {
            if (isNotNull(s)) {
                this.dataStateChangeCompleted(s);
            }
        }));
    }
    /**
     * Row click handler
     * @param row: selected row data
     */
    onGridRowClicked(row) {
        if (this.selectedRowIndex === row.id) {
            this.selectedRowIndex = -1;
        }
        else {
            this.selectedRowIndex = row.id;
        }
    }
    /**
     * checks row is selected or not
     * @returns boolean
     */
    hasRowSelected() {
        if (this.selectedRowIndex === -1) {
            return false;
        }
        return true;
    }
    /**
     *  returns selected row
     */
    getSelectedRow() {
        return this.dataSource.data.find(item => item[this.getPrimaryKeyName()] === this.selectedRowIndex.toString());
    }
    /**
     *  resets selected row
     */
    resetSelectedRow() {
        this.selectedRowIndex = -1;
    }
    /**
     *
     * @param event Fab Grid paginator change event
     */
    onPageChange(event) {
        this.currentState = { ...this.currentState, take: event.pageSize, skip: event.pageIndex * event.pageSize };
        this.pageSize = event.pageSize;
        this.stateSubject.next(this.getState());
    }
    /**
     * updates state for the table based on the filter selected
     *
     * @param event - object that carries filter changes recently made on the table
     * */
    onFilterChange(event) {
        this.currentState.skip = 0;
        this.currentState.filter = filterStateChange(event, this.currentState.filter);
        this.stateSubject.next(this.getState());
    }
    /**
     * updates state for the table based on the sort change
     *
     * @param event - object that carries sort changes recently made on the table
     * */
    onSortChange(event) {
        const sortData = { dir: event.direction, field: event.active };
        this.currentState = { ...this.currentState, sort: [sortData] };
        this.stateSubject.next(this.getState());
    }
    /**
     *  starts grid loading
     *
     */
    startGridLoading() {
        this.isGridLoading = true;
    }
    /**
     *  finishes grid loading
     */
    finishGridLoading() {
        this.isGridLoading = false;
    }
    /**
     * Clear all filter event
     */
    clearAllFilters() {
        this.tableFilters.toArray().forEach((x) => x.clearFilter());
        this.stateSubject.next(this.getState());
    }
    /**
     *  sets primary key name
     * @param primaryKeyName
     */
    setPrimaryKeyName(primaryKeyName) {
        this.primaryKeyName = primaryKeyName;
    }
    /**
     * gets primary key name
     * @protected
     */
    getPrimaryKeyName() {
        return this.primaryKeyName;
    }
    /**
     * adds filter to filter list
     * @param event - Add filter to the filter list.
     *
     */
    addFilter(filter) {
        let state = this.getState();
        if (isNotNull(state.filter)) {
            state = { ...state, filter: [...state.filter, filter] };
        }
        this.setState(state);
    }
    /**
     * Gets the current state.
     * @returns State object of the current state.
     */
    getState() {
        return this.currentState;
    }
    /**
     * Sets the state.
     *
     * @param state - The Fab State interface.
     */
    setState(state) {
        this.currentState = state;
        if (isNotNull(this.paginator)) {
            this.paginator.pageIndex = this.currentState.skip / this.currentState.take;
        }
    }
    /**
     * Sets the default state of with the given FabTableState.
     * This has to be called on ngAfterViewInit, so we have access to the column filters.
     *
     * @param defaultState Fab Table State.
     * @protected
     */
    setDefaultState(defaultState) {
        // Check to see if we are given a default state
        if (isNotNull(defaultState)) {
            // See if the default state has grid filters
            if (isNotEmptyArray(defaultState.filter)) {
                // For each default filter, iterate and find the fabric column filter component
                defaultState.filter.forEach(filter => {
                    // We look for the Fab Filter component loaded on the UI that has the same columnDef as the filter field in the default filter.
                    const fabFilterComponent = this.tableFilters.find(filterComponent => filterComponent.columnDef === filter.field);
                    // If we find the filter column component, we set the form values. Else we give a good warning.
                    if (isNotNull(fabFilterComponent)) {
                        fabFilterComponent.value.setValue(filter.value);
                        fabFilterComponent.operator.setValue(filter.operator);
                        fabFilterComponent.filterIsSelected = true;
                    }
                    else {
                        console.warn("We tried to set a default filter for: " + filter.field + " but no column filters exist with columnDef=" + filter.field + ".");
                    }
                });
            }
            // See if we are given any default sort options
            if (isNotEmptyArray(defaultState.sort)) {
                // Iterate through each default sort given and set the direction
                defaultState.sort.forEach(defaultSort => {
                    // Mat Table only allows to sort by one column, so we don't care about any sort objects that have '' as the direction.
                    if (isNotNull(defaultSort.dir)) {
                        // Look for the Mat Sortable that has a matching id. We use the field name to find the sort.
                        const matSortable = this.sort.sortables.get(defaultSort.field);
                        // Check if the grid has this sort column defined. If not, give a good error.
                        if (isNotNull(matSortable)) {
                            // Mat Sort only allows ASC or DESC, but our options are ASC, DESC, and ''. Default to asc.
                            let sortDirection = 'asc';
                            // Set to desc if the desired sort is desc
                            if (defaultSort.dir === 'desc') {
                                sortDirection = 'desc';
                            }
                            // Sets the sort direction from the default sort.
                            matSortable.start = sortDirection;
                            // Apply the desired sort object
                            this.sort.sortables.set(defaultSort.field, matSortable);
                            // Set the active column that is being sorted on the MatSort.
                            this.sort.active = defaultSort.field;
                            this.sort.start = sortDirection;
                            this.sort.direction = sortDirection;
                            // Cast to the MatSortHeader from the MatSort to set the proper arrow direction
                            const sortHeader = matSortable;
                            // Transition the animation to active and update the arrow direction.
                            sortHeader._setAnimationTransitionState({ toState: 'active' });
                            sortHeader._updateArrowDirection();
                        }
                        else {
                            console.warn("We tried to set a default sort for: " + defaultSort.field + " but no MatSort columns had that id specified.");
                        }
                    }
                });
            }
            const defaultTake = defaultState.take;
            const defaultSkip = defaultState.skip;
            // Set the desired default page size
            if (isNotNull(defaultTake) && defaultTake > 0) {
                this.pageSize = defaultState.take;
            }
            // Set current page index, setting them back to what page they were on.
            if (isNotNull(defaultState.skip) && defaultSkip > 0 && defaultTake > 0) {
                // Fabric does not save the page index, so we have to calculate the pageIndex.
                // The calculation for skip = pageIndex x pageSize(take).
                // Therefore, pageIndex = skip / pageSize(take).
                this.paginator.pageIndex = defaultSkip / defaultTake;
            }
            this.setState(defaultState);
        }
    }
    /**
     * Removes a column filter.
     *
     * @param field
     */
    removeFilter(field) {
        let fabTableState = this.getState();
        if (isNotNull(fabTableState) && Array.isArray(fabTableState.filter) && isEmptyArray(fabTableState.filter)) {
            fabTableState = {
                ...fabTableState, filter: fabTableState.filter.filter(filter => {
                    return filter.field !== field;
                })
            };
        }
    }
    /**
     * Finds the filter matching the field name
     *
     * @param field
     */
    getFilter(field) {
        let filter;
        const fabTableState = this.getState();
        if (isNotNull(fabTableState) && Array.isArray(fabTableState.filter) && isEmptyArray(fabTableState.filter)) {
            filter = fabTableState.filter.find(filter => {
                return filter.field === field;
            });
        }
        return filter;
    }
    /**
     * Resets the grid state.
     *
     * @param recordsPerPage Number of records per page.
     */
    resetState(recordsPerPage) {
        this.currentState = { ...this.defaultState, sort: [], filter: [] };
        if (isNotNull(recordsPerPage)) {
            this.currentState.take = recordsPerPage;
        }
    }
    /**
     * Set sort
     * @param sort
     */
    setSort(sort) {
        this.currentState.sort = sort;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: CommonFabGridComponent, deps: "invalid", target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.10", type: CommonFabGridComponent, viewQueries: [{ propertyName: "sort", first: true, predicate: MatSort, descendants: true, static: true }, { propertyName: "paginator", first: true, predicate: MatPaginator, descendants: true, static: true }, { propertyName: "tableFilters", predicate: ["tableFilter"], descendants: true }], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: CommonFabGridComponent, decorators: [{
            type: Directive
        }], ctorParameters: function () { return [{ type: undefined }]; }, propDecorators: { sort: [{
                type: ViewChild,
                args: [MatSort, { static: true }]
            }], paginator: [{
                type: ViewChild,
                args: [MatPaginator, { static: true }]
            }], tableFilters: [{
                type: ViewChildren,
                args: ['tableFilter']
            }] } });

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
// Common Grid

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CommonFabGridComponent };
